(function (root, factory) {
    if (typeof define === 'function' && define.amd) {
        // AMD
        define([], factory);
    } else if (typeof module === 'object' && module.exports) {
        // CommonJS
        module.exports = factory();
    } else {
        // Browser global
        root.repositionUtil = factory();
    }
}(typeof self !== 'undefined' ? self : this, function () {
    type RepositionOptions = {
        gap?: number;
    };

    type PositionType = 'top' | 'bottom';

    function getZoomLevel(zoomElement: HTMLElement | null): number {
        let zoom = 1;
        try {
            if (zoomElement) {
                const computedStyle = window.getComputedStyle(zoomElement) as any;
                const zoomStyle = computedStyle.zoom;
                if (zoomStyle) {
                    zoom = parseFloat(zoomStyle) || 1;
                }
            }
        } catch (e) {
            console.warn("Couldn't detect zoom, defaulting to 1:", e);
        }
        return zoom;
    }

    function isSafariBrowser(): boolean {
        return /^((?!chrome|android).)*safari/i.test(navigator.userAgent) || 
               (navigator.userAgent.includes('AppleWebKit') && 
                !navigator.userAgent.includes('Chrome') &&
                !navigator.userAgent.includes('Chromium'));
    }

    function repositionElement(
        input: HTMLElement,
        elementToPosition: HTMLElement,
        positionType: PositionType,
        zoomContainer: HTMLElement | null = null,
        options: RepositionOptions = {}
    ): void {
        const { gap = 0 } = options;
        const zoom = getZoomLevel(zoomContainer);

        if (zoom === 1) {
            return;
        }
        
        const isSafari = isSafariBrowser();
        const inputRect = input.getBoundingClientRect();
        const elementRect = elementToPosition.getBoundingClientRect();
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        const scrollLeft = window.scrollX || document.documentElement.scrollLeft;
    
        let adjustedTop, adjustedLeft;
        
        if (isSafari) {
            if (positionType === 'bottom') {
                adjustedTop = (inputRect.bottom + gap);
            } else { // top
                adjustedTop = (inputRect.top - elementRect.height - gap);
            }
            
            const inputCenter = inputRect.left + (inputRect.width / 2);
            adjustedLeft = (inputCenter - (elementRect.width / 2));
            
            // Account for scroll
            if (scrollTop > 0) {
                adjustedTop += scrollTop;
            }
            
            if (scrollLeft > 0) {
                adjustedLeft += scrollLeft;
            }
        } else {
            if (positionType === 'bottom') {
                const absoluteLeft = inputRect.left + scrollLeft;
                adjustedTop = (inputRect.bottom + scrollTop + gap) / zoom;
                adjustedLeft = absoluteLeft / zoom;
            } else {
                const inputTop = inputRect.top + scrollTop;
                const inputLeft = inputRect.left + scrollLeft;
                
                const adjustedElementHeight = elementRect.height * zoom;
                const adjustedElementWidth = elementRect.width * zoom;
                
                adjustedTop = (inputTop - adjustedElementHeight - gap) / zoom;
                const inputCenterX = inputLeft + inputRect.width / 2;
                const elementLeft = inputCenterX - adjustedElementWidth / 2;
                adjustedLeft = elementLeft / zoom;
            }
        }
    
        // Apply the calculated styles
        elementToPosition.style.position = 'absolute';
        elementToPosition.style.top = `${adjustedTop}px`;
        elementToPosition.style.left = `${adjustedLeft}px`;
        elementToPosition.style.zIndex = '9999';
        elementToPosition.style.visibility = 'visible';
    }

    function repositionElementBottomRelativeToInput(
        input: HTMLElement,
        elementToPosition: HTMLElement,
        zoomContainer: HTMLElement | null = null,
        options: RepositionOptions = {}
    ): void {
        repositionElement(input, elementToPosition, 'bottom', zoomContainer, options);
    }

    function repositionElementTopRelativeToInput(
        input: HTMLElement,
        elementToPosition: HTMLElement,
        zoomContainer: HTMLElement | null = null,
        options: RepositionOptions = {}
    ): void {
        repositionElement(input, elementToPosition, 'top', zoomContainer, options);
    }

    /**
     * Sets up a ResizeObserver on a specified element and performs an action on a target element when a resize is detected.
     *
     * @param {string} observedSelector - CSS selector for the element to observe.
     * @param {string} targetSelector - CSS selector for the element to modify.
     * @param {Function} action - Callback function to execute on resize. Receives the target element and the ResizeObserverEntry.
     */
    function setupResizeObserver(observedSelector, targetSelector, action) {
        const observedElement = document.querySelector(observedSelector);
        const targetElement = document.querySelector(targetSelector);
    
        if (!observedElement || !targetElement) {
        console.warn(`Element(s) not found: ${observedSelector}, ${targetSelector}`);
        return;
        }
    
        const resizeObserver = new ResizeObserver((entries) => {
        for (let entry of entries) {
            action(targetElement, entry);
        }
        });
    
        resizeObserver.observe(observedElement);
    }
  

    return {
        repositionElementBottomRelativeToInput,
        repositionElementTopRelativeToInput,
        setupResizeObserver
    };
}));